import Pusher from 'pusher-js'
import { getAuthorizationHeaders } from 'lib/helpers/getAuthorizationHeaders/getAuthorizationHeaders'
import { addAction, logError } from 'lib/tracking/datadog'
import { attachIdleDisconnect } from './helpers/attachIdleDisconnect/attachIdleDisconnect'
import { createPusherLink } from './helpers/createPusherLink/createPusherLink'

interface PusherLinkParams {
  token?: Maybe<string>
}

interface PusherStateChange {
  previous: string
  current: string
}

// Only one Pusher connection is active at a time, but the client (and its Pusher) is recreated on a
// business switch. Detach the previous idle listener before wiring the next so none stays bound to a
// discarded pusher.
let detachIdleDisconnect: (() => void) | undefined

export const pusherLink = ({ token }: PusherLinkParams = {}) => {
  try {
    const pusher = new Pusher(process.env.PUSHER_APP_KEY, {
      cluster: 'us3',
      authEndpoint: `${process.env.BACKEND_APP_URL}/pusher/auth`,
      auth: { headers: getAuthorizationHeaders({ externalToken: token }) },
    })

    pusher.connection.bind('error', (err: unknown) => {
      logError(err, { name: 'Pusher connection error' })
    })

    pusher.connection.bind('state_change', (states: PusherStateChange) => {
      addAction('Pusher connection state change', { from: states.previous, to: states.current })
    })

    pusher.bind_global((eventName: string, data: unknown) => {
      if (eventName === 'pusher:subscription_error') {
        logError(data, { name: 'Pusher subscription error' })
      }
    })

    detachIdleDisconnect?.()
    detachIdleDisconnect = attachIdleDisconnect({ pusher })

    return createPusherLink({ pusher })
  } catch (err) {
    logError(err, { name: 'Failed to initialize Pusher' })
    throw err
  }
}
